/**
 * Cascade Slot Game Runtime Store
 *
 * Zustand store managing all runtime state for cascade slot game play mode.
 * Uses cascade-slot.types for grid, wins, and game phases.
 *
 * Key Features:
 * - CascadeGamePhase state machine (idle -> spinning -> evaluating -> cascade loop)
 * - Balance and bet management with configurable bet levels
 * - Cascade/tumble tracking (level, tumbleWin, activeMultiplier)
 * - Free spins with retrigger support
 * - Auto-spin and turbo mode
 * - Spin history ring buffer (last 100 spins)
 *
 * NOTE: Uses immer + devtools middleware, NO persist.
 * Runtime state is ephemeral and reset on each play session.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';
import type {
  CascadeSlotConfig,
  CascadeGamePhase,
  GridCell,
  WinResult,
} from '@core/types/cascade-slot.types';
import {
  CascadeGamePhase as Phase,
  DEFAULT_CASCADE_SLOT_CONFIG,
} from '@core/types/cascade-slot.types';
import type { NormalizedBetResult, SlotApiSession } from '@core/types/slot-api.types';

const MAX_SPIN_HISTORY = 100;

// === Spin History Entry ===

export interface SpinHistoryEntry {
  spinNumber: number;
  timestamp: number;
  bet: number;
  totalWin: number;
  cascadeCount: number;
  freeSpinsTriggered: boolean;
}

// === State Interface ===

export interface CascadeSlotRuntimeState {
  // State machine
  gamePhase: CascadeGamePhase;

  // Grid
  grid: GridCell[][];

  // Economy
  balance: number;
  currentBet: number;
  winAmount: number;
  totalSpinWin: number;
  currentSpinWin: number;

  // Cascade tracking
  cascadeLevel: number;
  tumbleWin: number;
  activeMultiplier: number;

  // Free spins
  freeSpinsActive: boolean;
  freeSpinsRemaining: number;

  // Player controls
  autoSpinActive: boolean;
  autoSpinRemaining: number;
  turbo: boolean;

  // Mode & scatter
  activeMode: number;  // 0=Normal, 1=Ante Bet, 2=Buy Feature, 3=Buy Feature High
  previousMode: number;  // Tracks mode before buy feature for reversion
  scatterWin: number;

  // Timing
  stateTimer: number;

  // Config reference
  config: CascadeSlotConfig;

  // Last evaluated wins (for WinOverlay)
  lastWins: WinResult[];

  // History
  spinHistory: SpinHistoryEntry[];

  // Reel spin animation
  reelSpinResultGrid: GridCell[][] | null;
  reelSpinComplete: boolean;

  // Skip animations (force stop)
  skipAnimations: boolean;

  // API mode
  apiMode: boolean;
  apiSession: SlotApiSession | null;
  apiLoading: boolean;
  apiError: string | null;
  pendingBetResult: NormalizedBetResult | null;
}

// === Actions Interface ===

export interface CascadeSlotRuntimeActions {
  initialize(config?: CascadeSlotConfig): void;
  requestSpin(): void;
  setGrid(grid: GridCell[][]): void;
  setGamePhase(phase: CascadeGamePhase): void;
  addWin(amount: number): void;
  deductBet(): void;
  creditWin(): void;
  setBetLevel(bet: number): void;
  toggleTurbo(): void;
  startAutoSpin(count: number): void;
  stopAutoSpin(): void;
  buyFeature(modeId?: number): void;
  forceStop(): void;
  setFreeSpins(count: number): void;
  addMultiplier(value: number): void;
  resetMultiplier(): void;
  incrementCascade(): void;
  resetCascade(): void;
  resetCurrentSpinWin(): void;
  setCurrentSpinWin(amount: number): void;
  setActiveMode(mode: number): void;
  updateConfig(config: CascadeSlotConfig): void;
  resetWinCounters(): void;
  addScatterWin(amount: number): void;
  setLastWins(wins: WinResult[]): void;
  tick(deltaMs: number): void;
  reset(): void;
  // Reel spin actions
  setReelSpinResultGrid(grid: GridCell[][] | null): void;
  setReelSpinComplete(complete: boolean): void;
  setSkipAnimations(skip: boolean): void;

  // API mode actions
  setApiMode(enabled: boolean): void;
  setApiSession(session: SlotApiSession | null): void;
  setApiLoading(loading: boolean): void;
  setApiError(error: string | null): void;
  setPendingBetResult(result: NormalizedBetResult | null): void;
  syncBalanceFromApi(balance: number): void;
}

// === Store ===

interface CascadeSlotRuntimeStore
  extends CascadeSlotRuntimeState,
    CascadeSlotRuntimeActions {}

const createInitialState = (config: CascadeSlotConfig = DEFAULT_CASCADE_SLOT_CONFIG): CascadeSlotRuntimeState => ({
  gamePhase: Phase.Idle,
  grid: [],
  balance: config.startingBalance,
  currentBet: config.betLevels[0],
  winAmount: 0,
  totalSpinWin: 0,
  currentSpinWin: 0,
  cascadeLevel: 0,
  tumbleWin: 0,
  activeMultiplier: 0,
  freeSpinsActive: false,
  freeSpinsRemaining: 0,
  autoSpinActive: false,
  autoSpinRemaining: 0,
  turbo: false,
  activeMode: 0,
  previousMode: 0,
  scatterWin: 0,
  stateTimer: 0,
  config,
  lastWins: [],
  spinHistory: [],
  // Reel spin animation
  reelSpinResultGrid: null,
  reelSpinComplete: false,

  // Skip animations
  skipAnimations: false,

  // API mode
  apiMode: false,
  apiSession: null,
  apiLoading: false,
  apiError: null,
  pendingBetResult: null,
});

export const useSlotGameRuntimeStore = create<CascadeSlotRuntimeStore>()(
  devtools(
    immer((set, get) => ({
      ...createInitialState(),

      initialize: (config?: CascadeSlotConfig) => {
        const cfg = config || DEFAULT_CASCADE_SLOT_CONFIG;
        const prevApiMode = get().apiMode;
        const prevSession = get().apiSession;
        set((state) => {
          Object.assign(state, createInitialState(cfg));
          // Preserve API mode settings across initialize
          state.apiMode = prevApiMode;
          state.apiSession = prevSession;
          // Use API session values when available
          if (prevApiMode && prevSession) {
            state.balance = prevSession.balance;
            if (prevSession.betConfig.length > 0) {
              state.config = { ...state.config, betLevels: prevSession.betConfig };
              state.currentBet = prevSession.betConfig[0];
            }
          }
        });
      },

      requestSpin: () => {
        const { gamePhase, balance, currentBet, apiMode } = get();
        if (gamePhase !== Phase.Idle) return;
        if (!apiMode && balance < currentBet) return;

        set((state) => {
          state.gamePhase = Phase.Spinning;
          state.winAmount = 0;
          state.totalSpinWin = 0;
          state.currentSpinWin = 0;
          state.cascadeLevel = 0;
          state.tumbleWin = 0;
          state.activeMultiplier = 0;
          state.stateTimer = 0;
        });
      },

      setGrid: (grid: GridCell[][]) => {
        set((state) => {
          state.grid = grid;
        });
      },

      setGamePhase: (phase: CascadeGamePhase) => {
        set((state) => {
          state.gamePhase = phase;
          state.stateTimer = 0;
        });
      },

      addWin: (amount: number) => {
        set((state) => {
          state.winAmount += amount;
          state.tumbleWin += amount;
          state.totalSpinWin += amount;
        });
      },

      setCurrentSpinWin: (amount: number) => {
        set((state) => {
          state.currentSpinWin = amount;
        });
      },

      deductBet: () => {
        if (get().apiMode) return; // Server handles balance
        set((state) => {
          state.balance -= state.currentBet;
        });
      },

      creditWin: () => {
        const { totalSpinWin, spinHistory, apiMode } = get();
        set((state) => {
          if (!apiMode) {
            state.balance += state.totalSpinWin;
          }

          // Add to spin history
          const entry: SpinHistoryEntry = {
            spinNumber: state.spinHistory.length + 1,
            timestamp: Date.now(),
            bet: state.currentBet,
            totalWin: state.totalSpinWin,
            cascadeCount: state.cascadeLevel,
            freeSpinsTriggered: false,
          };
          state.spinHistory.push(entry);
          if (state.spinHistory.length > MAX_SPIN_HISTORY) {
            state.spinHistory.shift();
          }

          // Auto-spin decrement
          if (state.autoSpinActive && state.autoSpinRemaining > 0) {
            state.autoSpinRemaining -= 1;
            if (state.autoSpinRemaining <= 0) {
              state.autoSpinActive = false;
              state.autoSpinRemaining = 0;
            }
          }
        });
      },

      setBetLevel: (bet: number) => {
        const { config, gamePhase } = get();
        if (gamePhase !== Phase.Idle) return;
        if (!config.betLevels.includes(bet)) return;

        set((state) => {
          state.currentBet = bet;
        });
      },

      toggleTurbo: () => {
        set((state) => {
          state.turbo = !state.turbo;
        });
      },

      startAutoSpin: (count: number) => {
        if (count <= 0) return;
        set((state) => {
          state.autoSpinActive = true;
          state.autoSpinRemaining = count;
        });
      },

      stopAutoSpin: () => {
        set((state) => {
          state.autoSpinActive = false;
          state.autoSpinRemaining = 0;
        });
      },

      buyFeature: (modeId?: number) => {
        const { config, balance, gamePhase, activeMode, apiMode } = get();
        if (gamePhase !== Phase.Idle) return;
        if (!config.buyFeatureEnabled) return;

        const cost = get().currentBet * config.buyFeatureCostMultiplier;
        if (!apiMode && balance < cost) return;

        // Resolve buy feature mode: use provided modeId, or default to 2
        const resolvedMode = modeId ?? 2;

        set((state) => {
          if (!apiMode) {
            state.balance -= cost;
          }
          state.previousMode = activeMode;
          state.activeMode = resolvedMode;
          state.winAmount = 0;
          state.totalSpinWin = 0;
          state.currentSpinWin = 0;
          state.tumbleWin = 0;
          state.cascadeLevel = 0;
          state.activeMultiplier = 0;
          state.scatterWin = 0;

          if (apiMode) {
            // API mode: go straight to Spinning — let the API response determine free spins
            state.gamePhase = Phase.Spinning;
          } else {
            // Local mode: pre-set free spins for local simulation
            state.freeSpinsActive = true;
            state.freeSpinsRemaining = config.freeSpins.spinsAwarded;
            state.gamePhase = Phase.FreeSpinsIntro;
          }
          state.stateTimer = 0;
        });
      },

      forceStop: () => {
        set((state) => {
          state.autoSpinActive = false;
          state.autoSpinRemaining = 0;
          state.skipAnimations = true;
          if (state.gamePhase === Phase.ReelSpinning) {
            state.reelSpinComplete = true; // signal overlay to fast-stop
          }
          // Other phases: skipAnimations flag makes all timer checks pass immediately
        });
      },

      setFreeSpins: (count: number) => {
        set((state) => {
          if (count > 0) {
            state.freeSpinsActive = true;
            state.freeSpinsRemaining = count;
          } else {
            state.freeSpinsActive = false;
            state.freeSpinsRemaining = 0;
          }
        });
      },

      addMultiplier: (value: number) => {
        set((state) => {
          state.activeMultiplier += value;
        });
      },

      resetMultiplier: () => {
        set((state) => {
          state.activeMultiplier = 0;
        });
      },

      incrementCascade: () => {
        set((state) => {
          state.cascadeLevel += 1;
        });
      },

      resetCascade: () => {
        set((state) => {
          state.cascadeLevel = 0;
          state.tumbleWin = 0;
        });
      },

      resetCurrentSpinWin: () => {
        set((state) => {
          state.currentSpinWin = 0;
        });
      },

      setActiveMode: (mode: number) => {
        set((state) => {
          state.activeMode = mode;
        });
      },

      updateConfig: (config: CascadeSlotConfig) => {
        set((state) => {
          state.config = config;
        });
      },

      resetWinCounters: () => {
        set((state) => {
          state.totalSpinWin = 0;
          state.winAmount = 0;
          state.currentSpinWin = 0;
          state.scatterWin = 0;
        });
      },

      addScatterWin: (amount: number) => {
        set((state) => {
          state.scatterWin = amount;
        });
      },

      setLastWins: (wins: WinResult[]) => {
        set((state) => {
          state.lastWins = wins;
        });
      },

      tick: (deltaMs: number) => {
        set((state) => {
          state.stateTimer += deltaMs;
        });
      },

      reset: () => {
        const config = get().config;
        set((state) => {
          Object.assign(state, createInitialState(config));
        });
      },

      // Reel spin actions
      setReelSpinResultGrid: (grid: GridCell[][] | null) => {
        set((state) => {
          state.reelSpinResultGrid = grid;
        });
      },

      setReelSpinComplete: (complete: boolean) => {
        set((state) => {
          state.reelSpinComplete = complete;
        });
      },

      setSkipAnimations: (skip: boolean) => {
        set((state) => {
          state.skipAnimations = skip;
        });
      },

      // API mode actions
      setApiMode: (enabled: boolean) => {
        set((state) => {
          state.apiMode = enabled;
        });
      },

      setApiSession: (session: SlotApiSession | null) => {
        set((state) => {
          state.apiSession = session;
        });
      },

      setApiLoading: (loading: boolean) => {
        set((state) => {
          state.apiLoading = loading;
        });
      },

      setApiError: (error: string | null) => {
        set((state) => {
          state.apiError = error;
        });
      },

      setPendingBetResult: (result: NormalizedBetResult | null) => {
        set((state) => {
          state.pendingBetResult = result;
        });
      },

      syncBalanceFromApi: (balance: number) => {
        if (balance === undefined || balance === null || Number.isNaN(balance)) return;
        set((state) => {
          state.balance = balance;
        });
      },
    })),
    { name: 'CascadeSlotRuntime' }
  )
);
